import { ChatBubbleOutline } from '@mui/icons-material';
import { ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import classSet from 'react-classset';
import { useSelector } from 'react-redux';

import assistant from '@/assets/icons/vn-icon-assistant.svg';
import { useGlobalData } from '@/contexts/GlobalDataProvider';
import { clientSettingsSelector } from '@/store/slices/clientsettings';

import { applicationDataSelector } from '../../store/slices/application-data';
import { userSelector } from '../../store/slices/user';
import clientUtils from '../../utils/clientUtils';
import ChatModal from './ChatModal';

type Props = {
  newNavigation?: boolean;
};

const MODEL_HOST_URL = 'https://components.unitypredict.com';
const ChatWidget = ({ newNavigation }: Props) => {
  const widgetRef = useRef(null);

  const { isToggleSidebarOpen } = useSelector(clientSettingsSelector);
  const { user } = useSelector(userSelector);
  const { ouData } = useSelector(applicationDataSelector);
  const [visible, setVisible] = useState(false);
  const { globalData } = useGlobalData();

  const url = useMemo(() => {
    if (!ouData || !globalData) {
      return '';
    }

    const data = {
      ouId: ouData.id,
      ouName: ouData.ouName,
      userEmail: user.email,
      userName: `${user.given_name} ${user.family_name}`,
      hostUrl: window.location.origin,
      endChatCallback: true,
      hideChatCallback: true,
    };
    return `${MODEL_HOST_URL}/chatbot/${globalData.chatbotModelId}?${clientUtils.convertToQueryString(data)}`;
  }, [ouData, globalData]);

  // creating a ref 'id'
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setVisible(false);
      }
    }
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [widgetRef.current]);

  useEffect(() => {
    // Function to handle messages from the iframe
    const handleMessage = (event: MessageEvent) => {
      // Validate the origin of the message to ensure security
      if (event.origin !== MODEL_HOST_URL) {
        return;
      }
      // Check if the message is the callback trigger
      if (event.data.type === 'HideChatWindow' && event.data.payload.message === 'Close chatwindow') {
        setVisible(false);
      }
    };

    // Add event listener for messages from the iframe
    window.addEventListener('message', handleMessage);
    // Cleanup event listener on component unmount
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <ListItem
      disablePadding
      className={classSet({
        'chat-widget list-item': true,
        isToggleSidebarOpen,
      })}
      onClick={() => setVisible(!visible)}
      title="ViewNexa Assistant"
    >
      <ListItemButton className="item-btn">
        <ListItemIcon className="menu-icon">
          {newNavigation ? (
            <span className="icon">
              <img src={assistant} alt="Assistant" />
            </span>
          ) : (
            <span className="material-icons chat-icon">
              {' '}
              <ChatBubbleOutline />
            </span>
          )}
        </ListItemIcon>
        <ListItemText className="chat-icon-text" primary="ViewNexa Assistant" />
      </ListItemButton>
      <div className="chat-widget" ref={widgetRef}>
        {/* Call Modal Window */}
        <ChatModal visible={visible} url={url} />
      </div>
    </ListItem>
  );
};

export default ChatWidget;
